import type { ReportWindowKind } from '@/lib/reports';

/**
 * Friendly schedule model ⇄ five-field cron. The editor shows frequency / days / time and generates the cron;
 * anything it cannot express round-trips as a custom expression. Server-side `describeCron` / `nextCronRun`
 * (GET …/reports/schedule) stay the source of truth for what a cron means.
 */

export type Frequency = 'daily' | 'weekdays' | 'weekly' | 'monthly' | 'hourly' | 'custom';

export interface ScheduleModel {
  frequency: Frequency;
  /** weekly: 0 = Sunday … 6 = Saturday */
  weekdays: number[];
  /** monthly: 1 … 31 */
  monthDays: number[];
  hour: number;
  minute: number;
  /** custom: the raw expression */
  cron: string;
}

export const FREQUENCY_LABEL: Record<Frequency, string> = { daily: 'Every day', weekdays: 'Weekdays', weekly: 'Weekly', monthly: 'Monthly', hourly: 'Hourly', custom: 'Custom' };
export const FREQUENCY_ORDER: Frequency[] = ['weekdays', 'daily', 'weekly', 'monthly', 'hourly', 'custom'];
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DEFAULT_SCHEDULE: ScheduleModel = { frequency: 'weekdays', weekdays: [1], monthDays: [1], hour: 7, minute: 0, cron: '0 7 * * 1-5' };

const uniqSorted = (xs: number[]) => [...new Set(xs)].sort((a, b) => a - b);

/** Compress sorted integers into cron list syntax, using ranges for 3+ consecutive values (1,2,3,5 → 1-3,5). */
function cronList(values: number[]): string {
  const v = uniqSorted(values);
  const parts: string[] = [];
  for (let i = 0; i < v.length; ) {
    let j = i;
    while (j + 1 < v.length && v[j + 1] === (v[j] as number) + 1) j++;
    parts.push(j - i >= 2 ? `${v[i]}-${v[j]}` : v.slice(i, j + 1).join(','));
    i = j + 1;
  }
  return parts.join(',');
}

export function scheduleToCron(m: ScheduleModel): string {
  const h = Math.min(23, Math.max(0, m.hour | 0));
  const mi = Math.min(59, Math.max(0, m.minute | 0));
  switch (m.frequency) {
    case 'daily':
      return `${mi} ${h} * * *`;
    case 'weekdays':
      return `${mi} ${h} * * 1-5`;
    case 'weekly': {
      const days = uniqSorted(m.weekdays.filter((d) => d >= 0 && d <= 6));
      if (days.length === 0) return `${mi} ${h} * * 1`;
      if (days.length === 7) return `${mi} ${h} * * *`;
      return `${mi} ${h} * * ${cronList(days)}`;
    }
    case 'monthly': {
      const days = uniqSorted(m.monthDays.filter((d) => d >= 1 && d <= 31));
      return `${mi} ${h} ${days.length ? cronList(days) : 1} * *`;
    }
    case 'hourly':
      return `${mi} * * * *`;
    case 'custom':
    default:
      return m.cron.trim();
  }
}

const NAMES: Record<string, number> = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };

/** Expand a cron list field (`1-5`, `1,3,5`, `mon-fri`, `*`) into numbers; null when it uses steps or is out of range. */
function expand(field: string, min: number, max: number, names?: Record<string, number>): number[] | null {
  if (field === '*' || field === '?') return null;
  const out: number[] = [];
  const toNum = (t: string): number | null => {
    const k = t.toLowerCase().slice(0, 3);
    if (names && k in names) return names[k] as number;
    if (!/^\d+$/.test(t)) return null;
    const n = Number(t);
    return n === 7 && max === 6 ? 0 : n;
  };
  for (const part of field.split(',')) {
    if (part.includes('/')) return null;
    if (part.includes('-')) {
      const [a, b] = part.split('-');
      const lo = toNum(a ?? '');
      const hi = toNum(b ?? '');
      if (lo === null || hi === null || lo < min || hi > max || lo > hi) return null;
      for (let v = lo; v <= hi; v++) out.push(v);
    } else {
      const v = toNum(part);
      if (v === null || v < min || v > max) return null;
      out.push(v);
    }
  }
  return uniqSorted(out);
}

/** Recognise the shapes the builder produces; everything else becomes `custom` with the raw expression kept. */
export function cronToSchedule(cron: string | null | undefined): ScheduleModel {
  const raw = (cron ?? '').trim().replace(/\s+/g, ' ');
  const custom: ScheduleModel = { ...DEFAULT_SCHEDULE, frequency: 'custom', cron: raw || DEFAULT_SCHEDULE.cron };
  if (!raw) return { ...DEFAULT_SCHEDULE };
  const f = raw.split(' ');
  if (f.length !== 5) return custom;
  const [mi, h, dom, mon, dow] = f as [string, string, string, string, string];
  if (!/^\d{1,2}$/.test(mi) || Number(mi) > 59 || mon !== '*') return custom;
  const minute = Number(mi);
  if (h === '*') {
    if (dom === '*' && dow === '*') return { ...DEFAULT_SCHEDULE, frequency: 'hourly', minute, cron: raw };
    return custom;
  }
  if (!/^\d{1,2}$/.test(h) || Number(h) > 23) return custom;
  const hour = Number(h);
  const base = { ...DEFAULT_SCHEDULE, hour, minute, cron: raw };
  if (dom === '*' && (dow === '*' || dow === '?')) return { ...base, frequency: 'daily' };
  if (dom === '*') {
    const days = expand(dow, 0, 6, NAMES);
    if (!days) return custom;
    if (days.join(',') === '1,2,3,4,5') return { ...base, frequency: 'weekdays', weekdays: days };
    if (days.length === 7) return { ...base, frequency: 'daily', weekdays: days };
    return { ...base, frequency: 'weekly', weekdays: days };
  }
  if (dow === '*' || dow === '?') {
    const days = expand(dom, 1, 31);
    if (!days) return custom;
    return { ...base, frequency: 'monthly', monthDays: days };
  }
  return custom;
}

/** Local, instant description for the builder’s header while the server’s explanation loads. */
export function describeScheduleLocal(m: ScheduleModel): string {
  const t = fmtClock(m.hour, m.minute);
  switch (m.frequency) {
    case 'daily':
      return `Every day at ${t}`;
    case 'weekdays':
      return `Weekdays at ${t}`;
    case 'weekly': {
      const d = uniqSorted(m.weekdays);
      if (d.length === 0) return `Every Monday at ${t}`;
      if (d.length === 7) return `Every day at ${t}`;
      return `Every ${listOf(d.map((x) => WEEKDAY_LONG[x] ?? String(x)))} at ${t}`;
    }
    case 'monthly': {
      const d = uniqSorted(m.monthDays);
      return `On the ${listOf((d.length ? d : [1]).map(ordinal))} of every month at ${t}`;
    }
    case 'hourly':
      return m.minute === 0 ? 'Every hour, on the hour' : `Every hour at :${String(m.minute).padStart(2, '0')}`;
    case 'custom':
    default:
      return m.cron ? `Cron “${m.cron}”` : 'Custom schedule';
  }
}

export function fmtClock(h: number, m: number): string {
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}
export const ordinal = (n: number): string => `${n}${n % 10 === 1 && n !== 11 ? 'st' : n % 10 === 2 && n !== 12 ? 'nd' : n % 10 === 3 && n !== 13 ? 'rd' : 'th'}`;
function listOf(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The period that reads naturally for a cadence: weekly Monday → last week, monthly → last month, daily → since last run … */
export function suggestedWindow(m: ScheduleModel): ReportWindowKind {
  switch (m.frequency) {
    case 'weekly':
      return m.weekdays.length === 1 && m.weekdays[0] === 1 ? 'last_week' : 'week';
    case 'monthly':
      return m.monthDays.length === 1 && m.monthDays[0] === 1 ? 'last_month' : 'month';
    case 'daily':
      return 'yesterday';
    case 'weekdays':
    case 'hourly':
    default:
      return 'since_last';
  }
}

// ---------------------------------------------------------------- time zones

export interface TimeZoneOption {
  id: string;
  /** "New York" */
  city: string;
  /** "America" */
  region: string;
  /** "GMT-4 · EDT" at the given instant */
  offset: string;
  /** Minutes east of UTC, for sorting */
  offsetMinutes: number;
}

const COMMON_ZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Berlin', 'UTC'];

function supportedZones(): string[] {
  try {
    const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
    const list = intl.supportedValuesOf?.('timeZone');
    if (list && list.length > 0) return list.includes('UTC') ? list : [...list, 'UTC'];
  } catch {
    /* fall through */
  }
  return COMMON_ZONES;
}

export function tzOffsetLabel(tz: string, at = new Date()): { label: string; minutes: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(at);
    const off = parts.find((p) => p.type === 'timeZoneName')?.value ?? 'GMT';
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value ?? '';
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(off);
    const minutes = m ? (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : 0;
    const label = abbr && !/^GMT[+-]/.test(abbr) && abbr !== off ? `${off} · ${abbr}` : off;
    return { label, minutes };
  } catch {
    return { label: '', minutes: 0 };
  }
}

let zoneCache: TimeZoneOption[] | null = null;
/** Every IANA zone the browser knows, with a live offset; cached per page load. */
export function listTimeZones(): TimeZoneOption[] {
  if (zoneCache) return zoneCache;
  const now = new Date();
  zoneCache = supportedZones()
    .map((id) => {
      const [region = '', ...rest] = id.split('/');
      const city = (rest.join('/') || id).replace(/_/g, ' ');
      const o = tzOffsetLabel(id, now);
      return { id, city, region: rest.length ? region : '', offset: o.label, offsetMinutes: o.minutes };
    })
    .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id));
  return zoneCache;
}

export function isCommonZone(id: string): boolean {
  return COMMON_ZONES.includes(id);
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** "Mon, Sep 28, 8:00 AM" in a zone. */
export function fmtInZone(iso: string, tz: string, opts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString();
  }
}

/** Offset (minutes east of UTC) of a zone at an instant. */
function offsetMinutesAt(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(at);
  const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'), g('second'));
  return Math.round((asUtc - (at.getTime() - at.getMilliseconds())) / 60_000);
}

/** UTC instant of a wall-clock time in `tz` (DST-aware). `ymd` is "YYYY-MM-DD". */
export function zonedToUtc(tz: string, ymd: string, hour = 0, minute = 0): Date {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  const guess = Date.UTC(y, m - 1, d, hour, minute, 0);
  const zone = isValidTimeZone(tz) ? tz : 'UTC';
  let off = offsetMinutesAt(zone, new Date(guess));
  let result = guess - off * 60_000;
  const check = offsetMinutesAt(zone, new Date(result));
  if (check !== off) {
    off = check;
    result = guess - off * 60_000;
  }
  return new Date(result);
}

/** "YYYY-MM-DD" of an instant on the wall clock of `tz`. */
export function ymdInZone(at: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: isValidTimeZone(tz) ? tz : 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(at);
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '01';
  return `${g('year')}-${g('month')}-${g('day')}`;
}

export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export const COOLDOWN_PRESETS: Array<{ minutes: number; label: string; hint: string }> = [
  { minutes: 0, label: 'Right away', hint: 'A notice on the next check (about a minute).' },
  { minutes: 15, label: '15 min', hint: 'Batch a quarter hour of activity.' },
  { minutes: 60, label: 'Hourly', hint: 'At most one notice an hour.' },
  { minutes: 240, label: '4 hours', hint: 'A few times a day at most.' },
  { minutes: 1440, label: 'Daily', hint: 'One digest of events per day.' },
];
